import * as XLSX from 'xlsx';

/** Section line shape returned by GET /bookkeeping/income-statement/detail. */
export interface PlExportLine {
  entry_date: string;
  entry_number: string;
  invoice_number: string | null;
  /** Original uploaded statement file name when the voucher stems from a bank/card statement; else null. */
  bank_statement_file: string | null;
  counterparty: string | null;
  account_code: string;
  account_name: string;
  description: string;
  amount: number;
}

export type PlExportSectionKey = 'revenue' | 'cost' | 'operating_expenses' | 'other_income' | 'other_expenses';

export interface PlExportData {
  sections: Record<PlExportSectionKey, PlExportLine[]>;
  period: { start: string; end: string };
  view?: 'booked' | 'posted';
}

/** Statement order — sheet order matches the on-screen formula banner. */
const SECTIONS: { key: PlExportSectionKey; sheet: string; label: string }[] = [
  { key: 'revenue', sheet: 'Revenue', label: 'Revenue' },
  { key: 'cost', sheet: 'Direct Costs', label: 'Direct Costs' },
  { key: 'operating_expenses', sheet: 'Operating Expenses', label: 'Operating Expenses' },
  { key: 'other_income', sheet: 'Other Income', label: 'Other Income' },
  { key: 'other_expenses', sheet: 'Other Expenses', label: 'Other Expenses' },
];

// Columns: A Date · B Voucher No. · C Invoice No. · D Bank Statement File ·
// E Payer/Payee · F Code · G Account · H Description · I Amount · J Share %
const HEADER = ['Date', 'Voucher No.', 'Invoice No.', 'Bank Statement File', 'Payer/Payee', 'Code', 'Account', 'Description', 'Amount', 'Share %'];
const AMOUNT_COL = 'I';
const SHARE_COL = 'J';

const sum = (lines: PlExportLine[]) =>
  Math.round(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0) * 100) / 100;

/** A number cell carrying a live formula plus its cached value — SheetJS
 *  serializes both (<f>…</f><v>…</v>), so spreadsheets recalculate the formula
 *  on dependency changes while static previews show the cached number. */
const formulaCell = (v: number, f: string): XLSX.CellObject => ({ t: 'n', v, f });

/** One section sheet: lines grouped by account (code order, dates ascending),
 *  an account subtotal row per group, and a section total row. Subtotals and
 *  the total are SUM formulas over their exact ranges, and each subtotal
 *  carries its share of the section total. Returns the sheet and the address
 *  of its total cell for the Summary sheet's cross-sheet references. */
function buildSectionSheet(lines: PlExportLine[], sectionLabel: string): { sheet: XLSX.WorkSheet; totalRef: string } {
  const byAccount = new Map<string, PlExportLine[]>();
  for (const line of lines) {
    const key = line.account_code;
    if (!byAccount.has(key)) byAccount.set(key, []);
    byAccount.get(key)!.push(line);
  }
  const accountKeys = [...byAccount.keys()].sort();
  const total = sum(lines);

  const aoa: (string | number)[][] = [HEADER];
  const patches: { addr: string; cell: XLSX.CellObject; z?: string }[] = [];
  const subtotalRefs: string[] = [];
  let row = 2; // 1-based sheet row; the header occupies row 1

  for (const code of accountKeys) {
    const group = [...byAccount.get(code)!].sort((a, b) =>
      a.entry_date === b.entry_date ? a.entry_number.localeCompare(b.entry_number) : a.entry_date.localeCompare(b.entry_date));
    const accountName = group[0]?.account_name || code;
    const firstRow = row;
    for (const l of group) {
      aoa.push([
        l.entry_date, l.entry_number, l.invoice_number ?? '', l.bank_statement_file ?? '',
        l.counterparty ?? '', code, accountName, l.description ?? '', Number(l.amount) || 0, '',
      ]);
      row++;
    }
    const lastRow = row - 1;
    aoa.push(['Subtotal', '', '', '', '', code, accountName, '', '', '']);
    patches.push({ addr: `${AMOUNT_COL}${row}`, cell: formulaCell(sum(group), `SUM(${AMOUNT_COL}${firstRow}:${AMOUNT_COL}${lastRow})`) });
    subtotalRefs.push(`${AMOUNT_COL}${row}`);
    row++;
  }

  aoa.push([`Total ${sectionLabel}`, '', '', '', '', '', '', '', '', '']);
  const totalRow = row;
  // Sum the subtotal cells (never the whole column — that would double-count
  // the voucher rows); a section with no accounts keeps its plain zero.
  if (subtotalRefs.length > 0) {
    patches.push({ addr: `${AMOUNT_COL}${totalRow}`, cell: formulaCell(total, subtotalRefs.join('+')) });
    patches.push({ addr: `${SHARE_COL}${totalRow}`, cell: { t: 'n', v: 1, z: '0.0%' } });
    for (let i = 0; i < subtotalRefs.length; i++) {
      const subtotalRef = subtotalRefs[i];
      const subtotalRow = parseInt(subtotalRef.replace(/\D/g, ''), 10);
      const subtotalValue = sum(byAccount.get(accountKeys[i])!);
      patches.push({
        addr: `${SHARE_COL}${subtotalRow}`,
        cell: formulaCell(total !== 0 ? subtotalValue / total : 0, `${subtotalRef}/$${AMOUNT_COL}$${totalRow}`),
        z: '0.0%',
      });
    }
  } else {
    aoa[totalRow - 1][8] = total;
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  for (const { addr, cell, z } of patches) {
    if (z) cell.z = z;
    ws[addr] = cell;
  }
  ws['!cols'] = [{ wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 26 }, { wch: 28 }, { wch: 8 }, { wch: 24 }, { wch: 40 }, { wch: 14 }, { wch: 9 }];
  return { sheet: ws, totalRef: `${AMOUNT_COL}${totalRow}` };
}

/** Summary sheet: the formula blocks (Revenue − Direct Costs = GP − OpEx = OP
 *  + Other Income − Other Expenses = NP) as LIVE formulas — each section line
 *  references its sheet's total cell, the cascade is arithmetic. */
function buildSummarySheet(
  data: PlExportData,
  totalRefs: Partial<Record<PlExportSectionKey, string>>,
): XLSX.WorkSheet {
  const total = (key: PlExportSectionKey) => sum(data.sections[key] || []);
  const viewLabel = data.view === 'booked' ? 'Booked' : 'Posted';
  const rows: [string, number][] = [
    ['Revenue', total('revenue')],
    ['Less: Direct Costs', total('cost')],
    ['Gross Profit', total('revenue') - total('cost')],
    ['Less: Operating Expenses', total('operating_expenses')],
    ['Operating Profit', total('revenue') - total('cost') - total('operating_expenses')],
    ['Add: Other Income', total('other_income')],
    ['Less: Other Expenses', total('other_expenses')],
    ['Net Profit', total('revenue') - total('cost') - total('operating_expenses') + total('other_income') - total('other_expenses')],
  ];

  const aoa: (string | number)[][] = [
    [`Income Statement ${data.period.start} to ${data.period.end} (${viewLabel} view)`],
    [],
    ...rows.map(([label]) => [label, ''] as (string | number)[]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Sheet rows 3..10 in the order above; B3..B10. Cross-sheet refs are quoted
  // uniformly ('Direct Costs' has a space; quoting 'Revenue' is still legal).
  const sectionRef = (key: PlExportSectionKey, sheetName: string) =>
    `'${sheetName}'!${totalRefs[key] || 'I2'}`;
  const formulas: (string | undefined)[] = [
    sectionRef('revenue', 'Revenue'),
    sectionRef('cost', 'Direct Costs'),
    'B3-B4',
    sectionRef('operating_expenses', 'Operating Expenses'),
    'B5-B6',
    sectionRef('other_income', 'Other Income'),
    sectionRef('other_expenses', 'Other Expenses'),
    'B7+B8-B9',
  ];
  rows.forEach(([, v], i) => {
    const f = formulas[i];
    ws[`B${3 + i}`] = f ? formulaCell(v, f) : { t: 'n', v };
  });

  ws['!cols'] = [{ wch: 30 }, { wch: 14 }];
  return ws;
}

export function buildPlWorkbook(data: PlExportData): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const totalRefs: Partial<Record<PlExportSectionKey, string>> = {};
  const sheets: { sheet: XLSX.WorkSheet; name: string; section: (typeof SECTIONS)[number] }[] = [];
  for (const section of SECTIONS) {
    const { sheet, totalRef } = buildSectionSheet(data.sections[section.key] || [], section.label);
    totalRefs[section.key] = totalRef;
    sheets.push({ sheet, name: section.sheet, section });
  }
  // Summary first, then the section sheets in statement order.
  XLSX.utils.book_append_sheet(wb, buildSummarySheet(data, totalRefs), 'Summary');
  for (const { sheet, name } of sheets) XLSX.utils.book_append_sheet(wb, sheet, name);
  return wb;
}

/** Generate and download the .xlsx for the statement period. */
export function exportPlXlsx(data: PlExportData): void {
  const wb = buildPlWorkbook(data);
  XLSX.writeFile(wb, `income-statement_${data.period.start}_to_${data.period.end}${data.view === 'booked' ? '_booked' : ''}.xlsx`);
}
